'use client'

import { getAppArea } from '@/utils/analytics/app-area'
import { getAppEnvironment } from '@/utils/analytics/app-environment'
import { gaId, gtag, isAnalyticsEnabled } from '@/utils/analytics/gtag'
import { getPostHog } from '@/utils/analytics/posthog'
import { useActiveOrganization, useSession } from '@/utils/auth/auth-client'
import { appVersion } from '@/utils/version'

import { useEffect, useRef } from 'react'

import { usePathname } from 'next/navigation'

import * as Sentry from '@sentry/nextjs'

// Ties GA4 + PostHog to App Router navigation + auth state. Three
// responsibilities, each fired into both sinks (dual-fire during the GA→
// PostHog transition — see utils/analytics/posthog.ts):
//   1. Re-config GA with app_version + app_environment, and disable the
//      built-in initial page_view so we control all page_view events.
//      (PostHog gets the same stamps as super-properties at init.)
//   2. Fire a page_view / $pageview on every pathname change with app_area
//      attached.
//   3. Set user identity whenever session/active-org resolves — GA user_id +
//      user_properties, PostHog identify + person properties + flag-targeting
//      properties (mirrors atlas-mobile: active_org_id, user_role); reset on
//      sign-out.
//
// Safe to mount unconditionally — every effect early-exits if the sink isn't
// enabled (dev, missing key, prerender).

export function AnalyticsProvider() {
  const pathname = usePathname()
  const { data: session } = useSession()
  const { data: activeOrg } = useActiveOrganization()

  const configured = useRef(false)
  const lastUserId = useRef<string | null>(null)

  // One-time GA reconfiguration: stamp version + env on all subsequent events,
  // and disable the auto initial page_view so we own the navigation stream.
  useEffect(() => {
    if (!isAnalyticsEnabled()) return
    if (configured.current) return
    const id = gaId()
    if (!id) return

    gtag('config', id, {
      app_version: appVersion,
      app_environment: getAppEnvironment(),
      send_page_view: false
    })
    configured.current = true
  }, [])

  // page_view on every route change.
  useEffect(() => {
    if (!pathname) return

    if (isAnalyticsEnabled()) {
      gtag('event', 'page_view', {
        page_path: pathname,
        page_location: window.location.href,
        app_area: getAppArea(pathname),
        app_version: appVersion,
        app_environment: getAppEnvironment()
      })
    }

    // $pageview is PostHog's reserved pageview event — using it (rather than
    // a custom name) lights up the built-in Web Analytics dashboards.
    getPostHog()?.capture('$pageview', {
      $current_url: window.location.href,
      app_area: getAppArea(pathname)
    })
  }, [pathname])

  // Identity primitives hoisted out of the effect so the dependency list can
  // name exactly what the sync reads (exhaustive-deps flags a bare
  // `session?.user` otherwise, and depending on the object would re-fire on
  // every session refetch even when nothing relevant changed).
  const impersonatedBy = session?.session?.impersonatedBy ?? null
  // While impersonating, the session's user is the target, usually a customer.
  // Identifying as them would file the impersonator's clicks and session
  // replay under the customer, so the sinks keep the identity they already
  // have (the impersonator's, from before the swap).
  const userId = impersonatedBy ? null : (session?.user?.id ?? null)
  const userRole =
    (session?.user as { role?: string } | undefined)?.role ?? null
  const orgSlug = activeOrg?.slug ?? null
  const orgId = activeOrg?.id ?? null
  const orgName = activeOrg?.name ?? null

  // User identity for GA, PostHog, *and* Sentry. One effect = one place to
  // update when identity changes, and the three stay in sync.
  // Cleared on sign-out so neither system attributes activity to the previous
  // user. PII (email, IP) is intentionally NOT sent — instrumentation-client.ts
  // strips them in beforeSend regardless; we don't even pass them in here.
  // The branchy null-coalescing is inherent to "sync N sinks defensively":
  // fallow-ignore-next-line complexity
  useEffect(() => {
    const id = gaId()

    // --- Google Analytics ---
    if (isAnalyticsEnabled() && id) {
      if (userId !== lastUserId.current) {
        gtag('config', id, { user_id: userId ?? undefined })
      }
      gtag('set', 'user_properties', {
        org_slug: orgSlug,
        org_id: orgId,
        org_name: orgName,
        user_role: userRole ?? null
      })
    }

    // --- PostHog ---
    // identify() with the opaque user id (never email — matches GA + the
    // mobile app). Person properties power the People view; the same org/role
    // pair also goes to flag targeting + super-properties so feature flags
    // can roll out per-org and every event carries the active org.
    const ph = getPostHog()
    if (ph) {
      if (userId) {
        if (userId !== lastUserId.current) {
          ph.identify(userId)
        }
        ph.setPersonProperties({
          org_slug: orgSlug,
          org_id: orgId,
          org_name: orgName,
          user_role: userRole ?? null
        })
        ph.setPersonPropertiesForFlags({
          active_org_id: orgId,
          user_role: userRole ?? 'unknown',
          // The opaque better-auth id, so a flag can target ONE person.
          // Without it the smallest targetable unit is a whole org or role,
          // which makes "turn this on for just me" impossible — the normal way
          // to pilot a beta. Not PII and not new exposure: this is the same
          // value `identify()` already sends as the distinct_id a line above.
          // Email is still deliberately never sent.
          user_id: userId
        })
        ph.register({ active_org_id: orgId })
      } else if (lastUserId.current) {
        // Signed out — drop the identified person + super-properties so
        // post-signout events aren't attributed to the previous user.
        ph.reset()
      }
    }

    // --- Sentry ---
    // Setting user with only id (no email, no name, no ip) keeps PII out.
    // Org/role go on tags so they're searchable in Sentry's UI filters.
    if (userId) {
      Sentry.setUser({ id: userId })
    } else if (lastUserId.current) {
      // Was signed in, now signed out — clear so post-signout errors aren't
      // misattributed to the previous user.
      Sentry.setUser(null)
    }

    Sentry.setTag('org.slug', orgSlug ?? '')
    Sentry.setTag('org.id', orgId ?? '')
    Sentry.setTag('user.role', userRole ?? '')
    Sentry.setTag('app.version', appVersion)
    Sentry.setTag('app.environment', getAppEnvironment())

    lastUserId.current = userId
  }, [userId, userRole, orgId, orgSlug, orgName])

  // Tag impersonated traffic so adoption numbers can leave it out. The super
  // property persists in PostHog's storage, hence the explicit unregister once
  // the tab is back to its own identity.
  useEffect(() => {
    const ph = getPostHog()
    if (impersonatedBy) {
      ph?.register({ impersonating: true })
    } else {
      ph?.unregister('impersonating')
    }
    Sentry.setTag('impersonated_by', impersonatedBy ?? '')
  }, [impersonatedBy])

  return null
}
